// MessageThumbnail.jsx
import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { breakpoint } from '../style/variables.js';

const THUMBNAIL_LOAD_COMPLETE_TIMEOUT_IN_MSEC = 10000; // TODO: remote config

export const MessageThumbnail = ({
  username = '',
  originalCaptionText = '',
  thumbnail = '',
  objectFit = 'contain',
  size = 256,
  fetchPriority = undefined,
  onLoad = NULL_FUNCTION,
}) => {
  const altText = username ? `${username} : ${originalCaptionText}` : '';

  const [avifSrcSet, webpSrcSet, jpgSrcSet] = useMemo(
    () =>
      [MediaAssetFormat.AVIF, MediaAssetFormat.WEBP, MediaAssetFormat.JPG].map(
        format =>
          getPublicSrcSet({ href: thumbnail, size: size || 256, format })
      ),
    [thumbnail, size]
  );

  const effectiveDesktopSize = size * 2;

  // Ensure elements fill the desktop viewport so Google PageSpeed accurately detects and scores LCP
  const [avifSrcSetDesktop, webpSrcSetDesktop] = useMemo(
    () =>
      [MediaAssetFormat.AVIF, MediaAssetFormat.WEBP].map(format =>
        getPublicSrcSet({ href: thumbnail, size: effectiveDesktopSize, format })
      ),
    [thumbnail, effectiveDesktopSize]
  );

  const imageRef = useRef(null);
  const imageLoadedRef = useRef({ isLoaded: false });

  useEffect(() => {
    imageLoadedRef.current.isLoaded = false;

    // IMG.complete is supposed to be true even if there is an error,
    // but somehow it doesn't work as expected
    // if it's SSR and on mobile devices (neither iOS nor Android).
    // That's why we add a timeout mechanism in case the video couldn't play.
    const timer = setTimeout(() => {
      if (!imageLoadedRef.current.isLoaded) {
        imageLoadedRef.current.isLoaded = true;
        onLoad();
      }
    }, THUMBNAIL_LOAD_COMPLETE_TIMEOUT_IN_MSEC);
    return () => {
      clearTimeout(timer);
    };
  }, [thumbnail, onLoad]);

  useEffect(() => {
    // 1. The `onload` event doesn't emit as expected on SSR, that's why we add this hook as a fallback.
    // 2. Check for every render.
    if (imageRef?.current?.complete && !imageLoadedRef.current.isLoaded) {
      imageLoadedRef.current.isLoaded = true;
      onLoad();
    }
  });

  return (
    <Picture
      $objectFit={objectFit}
      onLoad={() => {
        if (!imageLoadedRef.current.isLoaded) {
          imageLoadedRef.current.isLoaded = true;
          onLoad();
        }
      }}
    >
      <DecryptionWrapper resourceUrl={jpgSrcSet.src}>
        <source
          srcSet={avifSrcSetDesktop.srcSet}
          type="image/avif"
          media={`(min-width: ${breakpoint.mobile}px)`}
        />
        <source
          srcSet={webpSrcSetDesktop.srcSet}
          type="image/webp"
          media={`(min-width: ${breakpoint.mobile}px)`}
        />
        <source srcSet={avifSrcSet.srcSet} type="image/avif" />
        <source srcSet={webpSrcSet.srcSet} type="image/webp" />

        <img
          {...jpgSrcSet}
          alt={altText}
          ref={imageRef}
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority={fetchPriority}
        />
      </DecryptionWrapper>
    </Picture>
  );
};

MessageThumbnail.propTypes = {
  username: PropTypes.string,
  originalCaptionText: PropTypes.string,
  thumbnail: PropTypes.string,
  objectFit: PropTypes.string,
  size: PropTypes.number,
  fetchPriority: PropTypes.string,
  onLoad: PropTypes.func,
};

const Picture = styled.picture`
  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ $objectFit }) => $objectFit};
  }
`;

export default MessageThumbnail;
